import { useEffect } from 'react';
import type { Dispatch } from '@reduxjs/toolkit';
import { play, pause, playNext, playPrevious, type SerializableVideo } from '../playerSlice';

interface UseMediaSessionOptions {
  dispatch: Dispatch;
  currentVideo: SerializableVideo | null;
  isPlaying: boolean;
}

const parseArtistAndTrack = (title: string): { artist: string; track: string } => {
  const match = title.match(/^(.+?)\s*[-–:]\s*(.+?)(\s*[[(].+?[\])])?\s*$/);
  if (match) {
    return {
      artist: match[1]?.trim() || 'Unknown Artist',
      track: match[2]?.trim() || title,
    };
  }
  return { artist: 'Unknown Artist', track: title };
};

const useMediaSession = ({ dispatch, currentVideo, isPlaying }: UseMediaSessionOptions): void => {
  const videoId = currentVideo?.videoId ?? null;
  const title = currentVideo?.title ?? '';
  const thumbnailUrl = currentVideo?.thumbnailUrl ?? '';

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;

    if (!videoId) {
      navigator.mediaSession.metadata = null;
      return;
    }

    const { artist, track } = parseArtistAndTrack(title);
    navigator.mediaSession.metadata = new MediaMetadata({
      title: track,
      artist,
      artwork: thumbnailUrl ? [{ src: thumbnailUrl }] : [],
    });
  }, [videoId, title, thumbnailUrl]);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;

    navigator.mediaSession.setActionHandler('play', () => dispatch(play()));
    navigator.mediaSession.setActionHandler('pause', () => dispatch(pause()));
    navigator.mediaSession.setActionHandler('nexttrack', () => dispatch(playNext()));
    navigator.mediaSession.setActionHandler('previoustrack', () => dispatch(playPrevious()));

    return () => {
      navigator.mediaSession.setActionHandler('play', null);
      navigator.mediaSession.setActionHandler('pause', null);
      navigator.mediaSession.setActionHandler('nexttrack', null);
      navigator.mediaSession.setActionHandler('previoustrack', null);
    };
  }, [dispatch]);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
  }, [isPlaying]);
};

export default useMediaSession;
